/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// 💬 Snowflake User Management SQL
/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// SQL strings backing /dashboards/user-management. Listing uses SHOW USERS
/// (real-time; ACCOUNT_USAGE.USERS has 45min – 3hr lag and would miss
/// freshly-created users), augmented with LOGIN_HISTORY for activity counts
/// and SHOW GRANTS TO USER for per-user role detail.
///
/// Privilege requirements: SHOW USERS for the whole account requires
/// MANAGE GRANTS, USERADMIN, ACCOUNTADMIN, or SECURITYADMIN. With a less
/// privileged role it returns only the current user. The page checks for
/// emptiness to surface a friendly "your role can't see other users"
/// message.

// //////////////////////////////////////////////////////////////////
// 💬 List queries
// //////////////////////////////////////////////////////////////////

/** All users, real-time. Run via RESULT_SCAN if you want to filter/sort
 *  server-side; the page does both client-side since the result set is
 *  small (typically <100 rows). */
export const showUsersSql = `SHOW USERS`

/** Per-user successful-login activity over the last 30 days. Joined into
 *  the user list client-side so we can show "X logins / 30d" + a true
 *  last-login timestamp. ACCOUNT_USAGE has 45min lag, which is fine for
 *  activity context (vs. user-existence which we keep real-time). */
export const userLoginActivity30dSql = `
SELECT
  USER_NAME                AS "userName",
  MAX(EVENT_TIMESTAMP)     AS "lastLogin",
  COUNT(*)                 AS "loginCount30d"
FROM SNOWFLAKE.ACCOUNT_USAGE.LOGIN_HISTORY
WHERE EVENT_TIMESTAMP >= DATEADD(DAY, -30, CURRENT_TIMESTAMP())
  AND IS_SUCCESS = 'YES'
GROUP BY USER_NAME
`

/** Failed login attempts in the last 24h, grouped by user. Powers a
 *  "needs attention" indicator on user rows when the account is being
 *  hammered by an attacker or has stale creds in some pipeline. */
export const userFailedLogins24hSql = `
SELECT
  USER_NAME                AS "userName",
  COUNT(*)                 AS "failedLogins24h",
  MAX(EVENT_TIMESTAMP)     AS "lastFailedAt",
  ANY_VALUE(ERROR_MESSAGE) AS "lastErrorMessage"
FROM SNOWFLAKE.ACCOUNT_USAGE.LOGIN_HISTORY
WHERE EVENT_TIMESTAMP >= DATEADD(HOUR, -24, CURRENT_TIMESTAMP())
  AND IS_SUCCESS = 'NO'
GROUP BY USER_NAME
`

// //////////////////////////////////////////////////////////////////
// 💬 Per-user detail queries
// //////////////////////////////////////////////////////////////////

/** Roles directly granted to a user. Returns one row per role —
 *  the drawer renders them as chips. Each row also carries
 *  granted_by/granted_on so we can show provenance.
 *
 *  Snowflake's SHOW GRANTS doesn't accept the user name as a bind
 *  parameter, so we interpolate it. We quote the identifier when
 *  it contains non-alphanumeric characters; uppercase identifiers
 *  bind without quoting. */
export function showGrantsToUserSql(userName: string): string {
  return `SHOW GRANTS TO USER ${quoteIdent(userName)}`
}

/** Recent query activity for a user. Last 30 days, count + most recent
 *  query timestamp + status. INFORMATION_SCHEMA is real-time; we cap
 *  RESULT_LIMIT to keep the call fast for high-activity users. */
export function userQueryActivity30dSql(userName: string): string {
  return `
SELECT
  COUNT(*)                                    AS "queryCount30d",
  MAX(START_TIME)                             AS "lastQueryAt",
  COUNT_IF(EXECUTION_STATUS = 'FAIL_PARSING') AS "parseFails",
  COUNT_IF(EXECUTION_STATUS = 'FAILED_WITH_ERROR') AS "execFails"
FROM TABLE(INFORMATION_SCHEMA.QUERY_HISTORY_BY_USER(
  USER_NAME => ${quoteString(userName)},
  RESULT_LIMIT => 10000
))
`
}

/** A user's most-recent successful logins. Limited to 10 rows for the
 *  drawer's "Recent Logins" section. */
export function userLoginHistorySql(userName: string): string {
  return `
SELECT
  EVENT_TIMESTAMP        AS "ts",
  CLIENT_IP              AS "clientIp",
  REPORTED_CLIENT_TYPE   AS "clientType",
  IS_SUCCESS             AS "success",
  ERROR_MESSAGE          AS "errorMessage"
FROM SNOWFLAKE.ACCOUNT_USAGE.LOGIN_HISTORY
WHERE USER_NAME = ${quoteString(userName)}
ORDER BY EVENT_TIMESTAMP DESC
LIMIT 10
`
}

// //////////////////////////////////////////////////////////////////
// 💬 Lookup queries for the Create User modal
// //////////////////////////////////////////////////////////////////

/** All warehouses the current role can see + use. Used to populate
 *  the "default warehouse" dropdown when creating a user. */
export const showWarehousesSql = `SHOW WAREHOUSES`

/** All roles the current role can grant. Used for the "default role"
 *  dropdown when creating a user. */
export const showRolesSql = `SHOW ROLES`

// //////////////////////////////////////////////////////////////////
// 💬 Mutating SQL helpers (DDL builders)
// //////////////////////////////////////////////////////////////////
//
// All mutations go through `useSnowflake({ query, transaction: 'auto' })`,
// which auto-wraps multi-statement requests in BEGIN/COMMIT — but DDL is
// auto-committing in Snowflake, so the wrapping is largely cosmetic for
// these. The single-statement helpers below are atomic on their own.

/** Build CREATE USER DDL with random password + MUST_CHANGE_PASSWORD. */
export function createUserDdl(args: {
  name: string
  loginName: string
  email?: string | null
  firstName?: string | null
  lastName?: string | null
  defaultRole?: string | null
  defaultWarehouse?: string | null
  defaultNamespace?: string | null
  type?: 'PERSON' | 'SERVICE' | 'LEGACY_SERVICE'
  password: string
  comment?: string | null
}): string {
  const parts: string[] = []
  parts.push(`CREATE USER ${quoteIdent(args.name)}`)
  parts.push(`  LOGIN_NAME = ${quoteString(args.loginName || args.name)}`)
  parts.push(`  PASSWORD = ${quoteString(args.password)}`)
  parts.push(`  MUST_CHANGE_PASSWORD = TRUE`)
  if (args.email)              parts.push(`  EMAIL = ${quoteString(args.email)}`)
  if (args.firstName)          parts.push(`  FIRST_NAME = ${quoteString(args.firstName)}`)
  if (args.lastName)           parts.push(`  LAST_NAME = ${quoteString(args.lastName)}`)
  if (args.defaultRole)        parts.push(`  DEFAULT_ROLE = ${quoteIdent(args.defaultRole)}`)
  if (args.defaultWarehouse)   parts.push(`  DEFAULT_WAREHOUSE = ${quoteIdent(args.defaultWarehouse)}`)
  if (args.defaultNamespace)   parts.push(`  DEFAULT_NAMESPACE = ${args.defaultNamespace}`) // already a qualified name
  if (args.type)               parts.push(`  TYPE = ${args.type}`)
  if (args.comment)            parts.push(`  COMMENT = ${quoteString(args.comment)}`)
  return parts.join('\n')
}

/** Reset a user's password. Always sets MUST_CHANGE_PASSWORD so the
 *  user is forced to rotate it on first login. */
export function resetPasswordDdl(userName: string, newPassword: string): string {
  return `ALTER USER ${quoteIdent(userName)} SET PASSWORD = ${quoteString(newPassword)} MUST_CHANGE_PASSWORD = TRUE`
}

/** Set or rotate the user's RSA public key. Pass `keyBody` as the PEM
 *  body (no `-----BEGIN/END-----` lines) — Snowflake's RSA_PUBLIC_KEY
 *  accepts the bare base64. */
export function setRsaPublicKeyDdl(userName: string, keyBody: string): string {
  return `ALTER USER ${quoteIdent(userName)} SET RSA_PUBLIC_KEY = ${quoteString(keyBody)}`
}

/** Remove the RSA public key from a user (revokes key-pair auth). */
export function unsetRsaPublicKeyDdl(userName: string): string {
  return `ALTER USER ${quoteIdent(userName)} UNSET RSA_PUBLIC_KEY`
}

/** Toggle the user's DISABLED flag. */
export function setDisabledDdl(userName: string, disabled: boolean): string {
  return `ALTER USER ${quoteIdent(userName)} SET DISABLED = ${disabled ? 'TRUE' : 'FALSE'}`
}

/**
 * Update one or more identity properties on an existing Snowflake user.
 *
 * Each field is sent through Snowflake's `ALTER USER ... SET <prop>`
 * (or `UNSET <prop>` when the operator cleared the field). We only
 * include properties that were ACTUALLY changed by the operator —
 * passing `undefined` for a field leaves it alone. An empty string
 * UNSETs the field (so the admin can clear an email, comment, etc.
 * back to NULL rather than re-typing the same value).
 *
 * Properties accepted (any subset):
 *   LOGIN_NAME, DISPLAY_NAME, FIRST_NAME, LAST_NAME, EMAIL, COMMENT
 *
 * Returns `null` when no properties were supplied so the caller can
 * skip the round-trip entirely.
 */
export function setUserIdentityDdl(userName: string, updates: {
  loginName?: string
  displayName?: string
  firstName?: string
  lastName?: string
  email?: string
  comment?: string
}): string | null {
  // Map of Snowflake parameter name → submitted value.
  const fields: Array<[string, string | undefined]> = [
    ['LOGIN_NAME',   updates.loginName],
    ['DISPLAY_NAME', updates.displayName],
    ['FIRST_NAME',   updates.firstName],
    ['LAST_NAME',    updates.lastName],
    ['EMAIL',        updates.email],
    ['COMMENT',      updates.comment],
  ]
  const setClauses: string[] = []
  const unsetClauses: string[] = []
  for (const [snowflakeName, value] of fields) {
    if (value === undefined) continue            // not changed
    if (value === '')        unsetClauses.push(snowflakeName)
    else                     setClauses.push(`${snowflakeName} = ${quoteString(value)}`)
  }
  if (!setClauses.length && !unsetClauses.length) return null
  // `ALTER USER` allows ONE of SET / UNSET per statement, not both.
  // Multi-statement payload (separated by `;`) handles the rare case
  // where the operator both set and cleared fields in one save.
  const statements: string[] = []
  if (setClauses.length) {
    statements.push(`ALTER USER ${quoteIdent(userName)} SET ${setClauses.join(', ')}`)
  }
  if (unsetClauses.length) {
    statements.push(`ALTER USER ${quoteIdent(userName)} UNSET ${unsetClauses.join(', ')}`)
  }
  return statements.join(';\n')
}

/** Drop a user. Admin-confirms-twice surface; this is irreversible. */
export function dropUserDdl(userName: string): string {
  return `DROP USER ${quoteIdent(userName)}`
}

// //////////////////////////////////////////////////////////////////
// 💬 Identifier / string quoting
// //////////////////////////////////////////////////////////////////

/** Quote a Snowflake identifier when it contains characters that aren't
 *  letters / digits / underscores, OR when it's lowercase (Snowflake
 *  uppercases unquoted identifiers, which would change semantics). Mirrors
 *  the convention in app/scripts/snowflake/users.js. */
export function quoteIdent(name: string): string {
  if (!name) return '""'
  // Already quoted — pass through.
  if (/^".*"$/.test(name)) return name
  // Safe-as-is: uppercase letters / digits / underscores only.
  if (/^[A-Z_][A-Z0-9_]*$/.test(name)) return name
  // Otherwise quote and escape any embedded double-quotes.
  return `"${name.replace(/"/g, '""')}"`
}

/** Snowflake string literal — single-quoted with embedded `'` doubled. */
export function quoteString(value: string): string {
  return `'${String(value).replace(/'/g, "''")}'`
}
